import {ListItem} from '@github-ui/list-view'
import type React from 'react'
import type {ComponentProps, ForwardedRef} from 'react'
import {graphql, useFragment} from 'react-relay'

import {Label} from './labels/Label'
import type {IssuePullRequestTitle$key} from './__generated__/IssuePullRequestTitle.graphql'
import {issueHovercardPath, issueLinkedPullRequestHovercardPath} from '@github-ui/paths'

const titleFragment = graphql`
  fragment IssuePullRequestTitle on IssueOrPullRequest
  @argumentDefinitions(labelPageSize: {type: "Int!", defaultValue: 10}) {
    __typename
    ... on Issue {
      number
      repository {
        name
        owner {
          login
        }
      }
      labels(first: $labelPageSize, orderBy: {field: NAME, direction: ASC}) {
        edges {
          node {
            ...Label
            name
            id
          }
        }
      }
    }
    ... on PullRequest {
      number
      repository {
        name
        owner {
          login
        }
      }
      labels(first: $labelPageSize, orderBy: {field: NAME, direction: ASC}) {
        edges {
          node {
            ...Label
            name
            id
          }
        }
      }
    }
  }
`

type IssuePullRequestTitleProps = {
  dataKey: IssuePullRequestTitle$key
  ref?: ForwardedRef<HTMLAnchorElement>
  href?: string
  target?: string
  onClick?: React.MouseEventHandler<HTMLElement>
  value: ComponentProps<typeof ListItem.Title>['value']
  leadingBadge?: ComponentProps<typeof ListItem.Title>['leadingBadge']
  onCopyTitle?: (event: ClipboardEvent) => void
  /**
   * Href getter for the label badge links
   * @param name - name of the label
   * @returns URL to the label
   */
  getLabelHref: (name: string) => string
  headerSx?: ComponentProps<typeof ListItem.Title>['headerSx']
}

export function IssuePullRequestTitle({
  dataKey,
  ref,
  href,
  target,
  onClick,
  value,
  leadingBadge,
  onCopyTitle,
  getLabelHref,
  headerSx,
}: IssuePullRequestTitleProps) {
  const data = useFragment(titleFragment, dataKey)

  const {number, repository, labels} =
    data.__typename === 'PullRequest' || data.__typename === 'Issue'
      ? data
      : {number: undefined, repository: undefined, labels: undefined}

  const hoverCardProps =
    repository?.owner && repository?.name
      ? {
          'data-hovercard-url': createHovercardUrl(data.__typename, repository.owner.login, repository.name, number),
          sx: {
            color: 'fg.default',
            ':hover': {
              color: 'accent.fg',
              textDecoration: 'none',
            },
          },
        }
      : {}

  const trailingLabels = (labels?.edges || []).flatMap(a => (a && a.node ? a.node : []))

  return (
    <ListItem.Title
      value={value}
      headerSx={{display: 'inline', ...(headerSx ?? {})}}
      anchorRef={ref}
      href={href}
      target={target}
      onClick={onClick}
      leadingBadge={leadingBadge}
      trailingBadges={trailingLabels.map(label => (
        <ListItem.TrailingBadge key={label.id} title={label.name}>
          <Label label={label} getLabelHref={getLabelHref} />
        </ListItem.TrailingBadge>
      ))}
      // Temp solution, TODO: clean up once https://github.com/github/collaboration-workflows-flex/issues/256 is merged,
      // see https://github.slack.com/archives/C05HD5GU6CA/p1700480160214479
      linkProps={onCopyTitle ? {onCopy: onCopyTitle, ...hoverCardProps} : hoverCardProps}
    />
  )
}

function createHovercardUrl(typeName: 'Issue' | 'PullRequest' | '%other', owner: string, repo: string, number: number) {
  switch (typeName) {
    case 'PullRequest':
      return issueLinkedPullRequestHovercardPath({
        owner,
        repo,
        pullRequestNumber: number,
      })
    case 'Issue':
      return issueHovercardPath({
        owner,
        repo,
        issueNumber: number,
      })
    default:
      return ''
  }
}

try{ IssuePullRequestTitle.displayName ||= 'IssuePullRequestTitle' } catch {}